import { useParamContext } from "client/components/params/ParamContext"
import { useGlobalContext } from "client/packages/utils/GlobalContext"
import {
  createContext,
  FC,
  MutableRefObject,
  ReactNode,
  useContext,
  useEffect,
  useState,
} from "react"

// Abstracts two behaviours into one fullscreen interface:
// 1. use the browser's native fullscreen API to enter fullscreen
// or
// 2. open a dedicated 'fullscreen' page, for when native fullscreen isn't supported (iOS)
//   ^ this option won't happen unless:
//   ^ a) the `fullscreenFallback` URL parameter is set to `true`
//   ^ b) or, the `allowIOSFullscreenFallbackPage` param is true (via react props)

export interface FullscreenState {
  isFullscreen: boolean
  isFullscreenAvailable: boolean
  fullscreenElement: HTMLElement
  enterFullscreen: () => void
  exitFullscreen: () => void
}

const getDedicatedPageURL = (url: Location) => {
  const newUrl = new URL(url.toString())
  // TODO: construct this url using our known url in the config rather than window.location
  newUrl.pathname = newUrl.pathname.replace("/channel/", "/embed/")
  newUrl.searchParams.append("fs", "true")
  return newUrl.href
}

// open a dedicated fullscreen page in the same tab
// Use _top to open this page outside an iframe if embedded.
// NOTE: Same tab prevents two Janus connections from being created.
export const openDedicatedPage = () => {
  window.open(getDedicatedPageURL(window.location), "_top")
}

export const isFullscreenSupported = () =>
  document.fullscreenEnabled === true

const FullscreenContext = createContext<FullscreenState | undefined>(
  undefined,
)

export type FullscreenProviderProps = {
  children: ReactNode
  fullscreenElement: MutableRefObject<HTMLElement>
  allowIOSFullscreenFallbackPage?: boolean
}

export const FullscreenProvider: FC<FullscreenProviderProps> = ({
  children,
  fullscreenElement,
  allowIOSFullscreenFallbackPage,
}) => {
  const [isFullscreen, setIsFullscreen] = useState(false)
  const {
    fs: isFullscreenFallbackPageOpen,
    fullscreenFallback: fullscreenFallbackPageUrlParamEnabled,
  } = useParamContext()

  // enabled by the channel page (via props) or via url param.
  const shouldUseFullscreenFallbackPage =
    allowIOSFullscreenFallbackPage || fullscreenFallbackPageUrlParamEnabled

  const enterFullscreen = () => {
    if (isFullscreenSupported()) {
      if (fullscreenElement.current === null || isFullscreen) return
      fullscreenElement.current.requestFullscreen()
    } else if (
      shouldUseFullscreenFallbackPage &&
      !isFullscreenFallbackPageOpen
    ) {
      openDedicatedPage()
    }
  }

  const exitFullscreen = () => {
    if (isFullscreen) {
      document.exitFullscreen()
    } else if (isFullscreenFallbackPageOpen) {
      window.history.back()
    }
  }

  const handler = (event) => {
    setIsFullscreen(!!event.currentTarget.fullscreenElement)
  }

  // If we ever exit lightbox view, exit fullscren too.
  const {
    state: { lightboxContent },
  } = useGlobalContext()
  useEffect(() => {
    if (isFullscreen && lightboxContent.contentType === "none") {
      exitFullscreen()
    }
  }, [lightboxContent.contentType])

  useEffect(() => {
    document.addEventListener("fullscreenchange", handler, false)
    return () => {
      document.removeEventListener("fullscreenchange", handler)
    }
  }, [])

  // Assemble the state to return.
  // This is what everyone who uses `useFullscreenContext` will receive
  const state: FullscreenState = {
    isFullscreen: isFullscreen || isFullscreenFallbackPageOpen,
    isFullscreenAvailable:
      isFullscreenSupported() ||
      shouldUseFullscreenFallbackPage ||
      isFullscreenFallbackPageOpen,
    fullscreenElement: fullscreenElement.current,
    enterFullscreen,
    exitFullscreen,
  }

  return (
    <FullscreenContext.Provider value={state}>
      {children}
    </FullscreenContext.Provider>
  )
}

export const useFullscreenContext = () => {
  const fullscreenContext = useContext(FullscreenContext)
  if (!fullscreenContext) {
    throw new Error(
      "No FullscreenContext.Provider found when calling useFullscreenContext.",
    )
  }
  return fullscreenContext
}
